import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { SupportTicket, SupportMetrics } from '@/types/support';

interface CursorMeta {
  per_page: number;
  next_cursor: string | null;
  prev_cursor: string | null;
  has_more: boolean;
}

export function useAdminSupportTickets(
  params: {
    status?: string;
    priority?: string;
    category?: string;
    sla_breached?: boolean;
    cursor?: string;
  } = {},
) {
  const searchParams = new URLSearchParams();
  if (params.status && params.status !== 'all') searchParams.set('status', params.status);
  if (params.priority && params.priority !== 'all') searchParams.set('priority', params.priority);
  if (params.category && params.category !== 'all') searchParams.set('category', params.category);
  if (params.sla_breached) searchParams.set('sla_breached', '1');
  if (params.cursor) searchParams.set('cursor', params.cursor);
  const qs = searchParams.toString();

  return useQuery({
    queryKey: ['admin', 'support-tickets', params],
    queryFn: async () => {
      const response = await api.get<SupportTicket[]>(
        `/admin/support/tickets${qs ? `?${qs}` : ''}`,
      );
      const meta = response.meta as unknown as CursorMeta | undefined;
      return {
        data: response.data ?? [],
        meta: meta ?? {
          per_page: 25,
          next_cursor: null,
          prev_cursor: null,
          has_more: false,
        },
      };
    },
  });
}

export function useAdminSupportTicket(id: string) {
  return useQuery({
    queryKey: ['admin', 'support-tickets', id],
    queryFn: async () => {
      const response = await api.get<SupportTicket>(`/admin/support/tickets/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useAdminSupportMetrics() {
  return useQuery({
    queryKey: ['admin', 'support-metrics'],
    queryFn: async () => {
      const response = await api.get<SupportMetrics>('/admin/support/metrics');
      return response.data;
    },
    staleTime: 60_000,
  });
}

export function useAdminSupportOpenCount() {
  return useQuery({
    queryKey: ['admin', 'support-open-count'],
    queryFn: async () => {
      const response = await api.get<{ count: number }>('/admin/support/open-count');
      return response.data.count;
    },
    refetchInterval: 60_000,
  });
}
